import { ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { BreakModeEnum } from '@/utils/dataAnalyzers';

// // Array/object fields need explicit JSON parse/stringify — plain localStorage getItem/setItem only handles strings.
const getStoredJson = (key, fallback) => {
  const raw = localStorage.getItem(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

export const useBreakStore = defineStore('break', () => {
  // // Break flags (Booleans, Nulls, & Undefineds)
  const isBreakReady = ref(localStorage.getItem('isBreakReady'));
  const isBreakBackgroundFree = ref(localStorage.getItem('isBreakBackgroundFree'));
  const isBreakBackgroundAnImage = ref(localStorage.getItem('isBreakBackgroundAnImage'));
  // // Transient (not persisted — meaningless across reloads): 'idle' | 'pending' | 'success' | 'error'.
  // // Distinguishes "still fetching" from "fetched, zero results" for breakAiRecommendations,
  // // since the array alone can't tell those two states apart.
  const breakAiRecommendationsStatus = ref('idle');
  // // Transient. Set alongside breakAiRecommendationsStatus='error' — an HTTP status code as a
  // // string (e.g. '400', '403') when the failure was a non-ok response, or 'CORS' when fetch()
  // // itself rejected with no response (network-level failure; browsers don't expose *why* a
  // // fetch failed this way, so this is a best-guess label, not a certainty). Null when the
  // // failure mode isn't known, or there was no failure at all.
  const breakAiRecommendationsErrorCode = ref(null);
  // // Break figures (Numbers)
  // // Break text (Strings)
  const breakGeneratedIdentifier = ref(localStorage.getItem('breakGeneratedIdentifier'));
  const breakType = ref(localStorage.getItem('breakType'));
  const breakTemplate = ref(localStorage.getItem('breakTemplate'));
  const breakTemplateUuid = ref(localStorage.getItem('breakTemplateUuid'));
  const breakUuid = ref(localStorage.getItem('breakUuid'));
  const breakTitle = ref(localStorage.getItem('breakTitle'));
  const breakSponsorName = ref(localStorage.getItem('breakSponsorName'));
  const breakPlaybackVideoSrc = ref(localStorage.getItem('breakPlaybackVideoSrc'));
  const breakPlaybackVideoDuration = ref(localStorage.getItem('breakPlaybackVideoDuration'));
  const breakBackgroundImageSrc = ref(localStorage.getItem('breakBackgroundImageSrc'));
  const breakBackgroundColor = ref(localStorage.getItem('breakBackgroundColor'));
  const breakHeadlineCopy = ref(localStorage.getItem('breakHeadlineCopy'));
  const breakHeadlineColor = ref(localStorage.getItem('breakHeadlineColor'));
  const breakTaglineCopy = ref(localStorage.getItem('breakTaglineCopy'));
  const breakTaglineColor = ref(localStorage.getItem('breakTaglineColor'));
  const breakScreenPlacement = ref(localStorage.getItem('breakScreenPlacement'));
  const breakCorrectOption = ref('');
  const breakTriviaTidbit = ref(localStorage.getItem('breakTriviaTidbit'));
  const breakAiTerm = ref(localStorage.getItem('breakAiTerm'));
  const breakAiType = ref(localStorage.getItem('breakAiType'));
  const breakAiCriteria = ref(localStorage.getItem('breakAiCriteria'));
  const breakDisplayMode = ref(localStorage.getItem('breakDisplayMode'));
  // // Break lists (Arrays)
  const breakSponsorLogos = ref(getStoredJson('breakSponsorLogos', []));
  const breakProducts = ref(getStoredJson('breakProducts', []));
  const breakOptions = ref(getStoredJson('breakOptions', []));
  const breakHotspots = ref(getStoredJson('breakHotspots', []));
  const breakCategories = ref(getStoredJson('breakCategories', []));
  const breakAiRecommendations = ref(getStoredJson('breakAiRecommendations', []));
  // // Break dictionaries (Objects)
  const breakSearchParameters = ref(getStoredJson('breakSearchParameters', {}));
  // // QR / Product
  const qrIdent = ref(localStorage.getItem('qrIdent'));
  const hasProductBeenPurchased = ref(localStorage.getItem('hasProductBeenPurchased'));

  // // Actions

  const blankBreak = () => {
    // console.log(' BLANK BREAK');

    isBreakReady.value = false;
    isBreakBackgroundFree.value = false;
    isBreakBackgroundAnImage.value = false;
    breakAiRecommendationsStatus.value = 'idle';
    breakAiRecommendationsErrorCode.value = null;
    breakGeneratedIdentifier.value = '';
    breakType.value = '';
    breakTemplate.value = '';
    breakTemplateUuid.value = '';
    breakUuid.value = '';
    breakTitle.value = '';
    breakSponsorName.value = '';
    breakPlaybackVideoSrc.value = '';
    breakPlaybackVideoDuration.value = '';
    breakBackgroundImageSrc.value = '';
    breakBackgroundColor.value = '';
    breakHeadlineCopy.value = '';
    breakHeadlineColor.value = '';
    breakTaglineCopy.value = '';
    breakTaglineColor.value = '';
    breakScreenPlacement.value = '';
    breakCorrectOption.value = '';
    breakTriviaTidbit.value = '';
    breakAiTerm.value = '';
    breakAiType.value = '';
    breakAiCriteria.value = '';
    breakSponsorLogos.value = [];
    breakProducts.value = [];
    breakOptions.value = [];
    breakHotspots.value = [];
    breakCategories.value = [];
    breakAiRecommendations.value = [];
    breakDisplayMode.value = BreakModeEnum.DEFAULT;
    breakSearchParameters.value = {};
    qrIdent.value = '';
    hasProductBeenPurchased.value = false;
  };

  // // Getters

  //

  // // Watchers
  watch(isBreakReady, () => {
    localStorage.setItem('isBreakReady', isBreakReady.value);
  });

  watch(isBreakBackgroundFree, () => {
    localStorage.setItem('isBreakBackgroundFree', isBreakBackgroundFree.value);
  });

  watch(isBreakBackgroundAnImage, () => {
    localStorage.setItem('isBreakBackgroundAnImage', isBreakBackgroundAnImage.value);
  });

  watch(breakGeneratedIdentifier, () => {
    localStorage.setItem('breakGeneratedIdentifier', breakGeneratedIdentifier.value);
  });

  watch(breakType, () => {
    localStorage.setItem('breakType', breakType.value);
  });

  watch(breakTemplate, () => {
    localStorage.setItem('breakTemplate', breakTemplate.value);
  });

  watch(breakTemplateUuid, () => {
    localStorage.setItem('breakTemplateUuid', breakTemplateUuid.value);
  });

  watch(breakUuid, () => {
    localStorage.setItem('breakUuid', breakUuid.value);
  });

  watch(breakTitle, () => {
    localStorage.setItem('breakTitle', breakTitle.value);
  });

  watch(breakSponsorName, () => {
    localStorage.setItem('breakSponsorName', breakSponsorName.value);
  });

  watch(breakPlaybackVideoSrc, () => {
    localStorage.setItem('breakPlaybackVideoSrc', breakPlaybackVideoSrc.value);
  });

  watch(breakPlaybackVideoDuration, () => {
    localStorage.setItem('breakPlaybackVideoDuration', breakPlaybackVideoDuration.value);
  });

  watch(breakBackgroundImageSrc, () => {
    localStorage.setItem('breakBackgroundImageSrc', breakBackgroundImageSrc.value);
  });

  watch(breakBackgroundColor, () => {
    localStorage.setItem('breakBackgroundColor', breakBackgroundColor.value);
  });

  watch(breakHeadlineCopy, () => {
    localStorage.setItem('breakHeadlineCopy', breakHeadlineCopy.value);
  });

  watch(breakHeadlineColor, () => {
    localStorage.setItem('breakHeadlineColor', breakHeadlineColor.value);
  });

  watch(breakTaglineCopy, () => {
    localStorage.setItem('breakTaglineCopy', breakTaglineCopy.value);
  });

  watch(breakTaglineColor, () => {
    localStorage.setItem('breakTaglineColor', breakTaglineColor.value);
  });

  watch(breakScreenPlacement, () => {
    localStorage.setItem('breakScreenPlacement', breakScreenPlacement.value);
  });

  watch(breakTriviaTidbit, () => {
    localStorage.setItem('breakTriviaTidbit', breakTriviaTidbit.value);
  });

  watch(breakAiTerm, () => {
    localStorage.setItem('breakAiTerm', breakAiTerm.value);
  });

  watch(breakAiType, () => {
    localStorage.setItem('breakAiType', breakAiType.value);
  });

  watch(breakAiCriteria, () => {
    localStorage.setItem('breakAiCriteria', breakAiCriteria.value);
  });

  watch(breakSponsorLogos, () => {
    localStorage.setItem('breakSponsorLogos', JSON.stringify(breakSponsorLogos.value));
  });

  watch(breakProducts, () => {
    localStorage.setItem('breakProducts', JSON.stringify(breakProducts.value));
  });

  watch(breakOptions, () => {
    localStorage.setItem('breakOptions', JSON.stringify(breakOptions.value));
  });

  watch(breakHotspots, () => {
    localStorage.setItem('breakHotspots', JSON.stringify(breakHotspots.value));
  });

  watch(breakCategories, () => {
    localStorage.setItem('breakCategories', JSON.stringify(breakCategories.value));
  });

  watch(breakAiRecommendations, () => {
    localStorage.setItem('breakAiRecommendations', JSON.stringify(breakAiRecommendations.value));
  });

  watch(qrIdent, () => {
    localStorage.setItem('qrIdent', qrIdent.value);
  });

  watch(breakDisplayMode, () => {
    localStorage.setItem('breakDisplayMode', breakDisplayMode.value);
  });

  watch(breakSearchParameters, () => {
    localStorage.setItem('breakSearchParameters', JSON.stringify(breakSearchParameters.value));
  });

  watch(hasProductBeenPurchased, () => {
    localStorage.setItem('hasProductBeenPurchased', hasProductBeenPurchased.value);
  });

  return {
    isBreakReady,
    isBreakBackgroundFree,
    isBreakBackgroundAnImage,
    breakAiRecommendationsStatus,
    breakAiRecommendationsErrorCode,
    breakGeneratedIdentifier,
    breakType,
    breakTemplate,
    breakTemplateUuid,
    breakUuid,
    breakTitle,
    breakSponsorName,
    breakPlaybackVideoSrc,
    breakPlaybackVideoDuration,
    breakBackgroundImageSrc,
    breakBackgroundColor,
    breakHeadlineCopy,
    breakHeadlineColor,
    breakTaglineCopy,
    breakTaglineColor,
    breakScreenPlacement,
    breakCorrectOption,
    breakTriviaTidbit,
    breakAiTerm,
    breakAiType,
    breakAiCriteria,
    breakSponsorLogos,
    breakProducts,
    breakOptions,
    breakHotspots,
    breakCategories,
    breakAiRecommendations,
    breakDisplayMode,
    breakSearchParameters,
    qrIdent,
    hasProductBeenPurchased,
    blankBreak,
  };
});
